import { useEffect, useState, useCallback } from "react";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  GearSix,
  PlugsConnected,
  DeviceMobile,
  UserPlus,
  Trash,
  Copy,
  CheckCircle,
} from "@phosphor-icons/react";

function Field({ label, children, hint }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
        {label}
      </Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default function Settings({ isAdmin }) {
  const [status, setStatus] = useState(null);
  const [uispUrl, setUispUrl] = useState("");
  const [uispKey, setUispKey] = useState("");
  const [twSid, setTwSid] = useState("");
  const [twToken, setTwToken] = useState("");
  const [twFrom, setTwFrom] = useState("");
  const [savingConn, setSavingConn] = useState(false);

  const [users, setUsers] = useState([]);
  const [nu, setNu] = useState({ name: "", email: "", password: "" });
  const [addingUser, setAddingUser] = useState(false);
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${API}/twilio/inbound`;

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setStatus(data);
      setUispUrl(data.uisp_url || "");
      setTwSid(data.twilio_account_sid || "");
      setTwFrom(data.twilio_from_number || "");
    } catch (e) {
      /* silent */
    }
  }, []);

  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      /* silent */
    }
  }, [isAdmin]);

  useEffect(() => {
    loadStatus();
    loadUsers();
  }, [loadStatus, loadUsers]);

  async function saveConnections() {
    setSavingConn(true);
    try {
      const payload = { uisp_url: uispUrl, twilio_from_number: twFrom, twilio_account_sid: twSid };
      if (uispKey) payload.uisp_app_key = uispKey;
      if (twToken) payload.twilio_auth_token = twToken;
      await api.put("/settings", payload);
      toast.success("Connection settings saved");
      setUispKey("");
      setTwToken("");
      await loadStatus();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingConn(false);
    }
  }

  async function addUser() {
    if (!nu.email || !nu.password) return toast.error("Email and password required");
    setAddingUser(true);
    try {
      await api.post("/users", nu);
      toast.success("User created");
      setNu({ name: "", email: "", password: "" });
      await loadUsers();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAddingUser(false);
    }
  }

  async function removeUser(id) {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User removed");
      await loadUsers();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function copyWebhook() {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="h-16 flex items-center px-6 border-b border-border gap-3">
        <GearSix size={22} weight="fill" className="text-primary" />
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl space-y-6">
          {!isAdmin && (
            <div className="text-sm text-muted-foreground border border-border rounded-sm p-3">
              You are viewing settings in read-only mode. Only admins can modify connections and users.
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* UISP */}
            <div className="border border-border rounded-sm p-5 bg-card/40 space-y-4">
              <div className="flex items-center gap-2">
                <PlugsConnected size={18} className="text-primary" />
                <h2 className="text-sm font-semibold tracking-tight uppercase tracking-[0.1em]">
                  UISP CRM
                </h2>
                <StatusPill ok={status?.uisp_url && status?.uisp_app_key_set} />
              </div>
              <Field label="API URL" hint="e.g. https://your-instance.uisp.com">
                <Input
                  data-testid="uisp-url-input"
                  value={uispUrl}
                  onChange={(e) => setUispUrl(e.target.value)}
                  placeholder="https://your-instance.uisp.com"
                  className="rounded-sm font-mono text-xs"
                  disabled={!isAdmin}
                />
              </Field>
              <Field label="App Key" hint={status?.uisp_app_key_set ? "A key is saved. Leave blank to keep it." : "From CRM → System → Security → App Keys"}>
                <Input
                  data-testid="uisp-key-input"
                  type="password"
                  value={uispKey}
                  onChange={(e) => setUispKey(e.target.value)}
                  placeholder={status?.uisp_app_key_set ? "••••••••••••" : "X-Auth-App-Key"}
                  className="rounded-sm font-mono text-xs"
                  disabled={!isAdmin}
                />
              </Field>
            </div>

            {/* Twilio */}
            <div className="border border-border rounded-sm p-5 bg-card/40 space-y-4">
              <div className="flex items-center gap-2">
                <DeviceMobile size={18} className="text-primary" />
                <h2 className="text-sm font-semibold uppercase tracking-[0.1em]">Twilio</h2>
                <StatusPill ok={status?.twilio_account_sid && status?.twilio_auth_token_set && status?.twilio_from_number} />
              </div>
              <Field label="Account SID">
                <Input
                  data-testid="twilio-sid-input"
                  value={twSid}
                  onChange={(e) => setTwSid(e.target.value)}
                  placeholder="ACxxxxxxxx"
                  className="rounded-sm font-mono text-xs"
                  disabled={!isAdmin}
                />
              </Field>
              <Field label="Auth Token" hint={status?.twilio_auth_token_set ? "A token is saved. Leave blank to keep it." : ""}>
                <Input
                  data-testid="twilio-token-input"
                  type="password"
                  value={twToken}
                  onChange={(e) => setTwToken(e.target.value)}
                  placeholder={status?.twilio_auth_token_set ? "••••••••••••" : "Auth token"}
                  className="rounded-sm font-mono text-xs"
                  disabled={!isAdmin}
                />
              </Field>
              <Field label="From Number" hint="E.164 format, e.g. +18885551234">
                <Input
                  data-testid="twilio-from-input"
                  value={twFrom}
                  onChange={(e) => setTwFrom(e.target.value)}
                  placeholder="+18885551234"
                  className="rounded-sm font-mono text-xs"
                  disabled={!isAdmin}
                />
              </Field>
            </div>
          </div>

          {isAdmin && (
            <div className="flex justify-end">
              <Button
                data-testid="save-connections-button"
                onClick={saveConnections}
                disabled={savingConn}
                className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {savingConn ? "Saving…" : "Save connections"}
              </Button>
            </div>
          )}

          {/* Inbound webhook */}
          <div className="border border-border rounded-sm p-5 bg-card/40 space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-[0.1em]">Inbound SMS Webhook</h2>
            <p className="text-xs text-muted-foreground">
              In your Twilio phone number config, set the messaging webhook (HTTP POST) to this URL so replies flow into the inbox:
            </p>
            <div className="flex items-center gap-2">
              <code
                data-testid="webhook-url"
                className="flex-1 text-xs bg-secondary border border-border rounded-sm px-3 py-2 font-mono truncate"
              >
                {webhookUrl}
              </code>
              <Button
                data-testid="copy-webhook-button"
                onClick={copyWebhook}
                variant="secondary"
                size="icon"
                className="rounded-sm h-9 w-9 shrink-0"
              >
                {copied ? <CheckCircle size={16} className="text-[hsl(var(--success))]" /> : <Copy size={16} />}
              </Button>
            </div>
          </div>

          {/* Users */}
          {isAdmin && (
            <div className="border border-border rounded-sm p-5 bg-card/40 space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-[0.1em]">Users</h2>
              <div className="space-y-1">
                {users.map((u) => (
                  <div
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    className="flex items-center gap-3 py-2 border-b border-border last:border-0"
                  >
                    <div className="h-8 w-8 rounded-sm bg-secondary border border-border flex items-center justify-center text-xs font-semibold">
                      {(u.name || u.email)[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{u.name || u.email}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{u.role}</span>
                    {u.role !== "admin" && (
                      <Button
                        data-testid={`delete-user-${u.id}`}
                        onClick={() => removeUser(u.id)}
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 rounded-sm text-muted-foreground hover:text-destructive"
                      >
                        <Trash size={16} />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <Separator />

              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                <UserPlus size={14} /> Add user
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Input
                  data-testid="new-user-name"
                  value={nu.name}
                  onChange={(e) => setNu({ ...nu, name: e.target.value })}
                  placeholder="Name"
                  className="rounded-sm"
                />
                <Input
                  data-testid="new-user-email"
                  value={nu.email}
                  onChange={(e) => setNu({ ...nu, email: e.target.value })}
                  placeholder="Email"
                  className="rounded-sm"
                />
                <Input
                  data-testid="new-user-password"
                  type="password"
                  value={nu.password}
                  onChange={(e) => setNu({ ...nu, password: e.target.value })}
                  placeholder="Password"
                  className="rounded-sm"
                />
              </div>
              <div className="flex justify-end">
                <Button
                  data-testid="add-user-button"
                  onClick={addUser}
                  disabled={addingUser}
                  variant="secondary"
                  className="rounded-sm gap-2"
                >
                  <UserPlus size={16} /> {addingUser ? "Adding…" : "Add user"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ ok }) {
  return (
    <span
      className={`ml-auto text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
        ok
          ? "text-[hsl(var(--success))] border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10"
          : "text-muted-foreground border-border bg-secondary"
      }`}
    >
      {ok ? "Connected" : "Not set"}
    </span>
  );
}
